<template>
  <v-dialog v-model="dialog" max-width="320">
    <v-card v-if="item">
      <div class="qty-head">
        <div>{{ actionMessage }}</div>
        <div class="qty-head__sub">{{ label }}</div>
      </div>

      <!-- Badge carries the line's total, so the ceiling is always on screen
           without needing a separate hint line. -->
      <div class="qty-entry">
        <span class="qty-entry__badge">Qty: {{ totalQuantity }}</span>
        <span class="qty-entry__value" :class="{ 'qty-entry__value--capped': capped }">
          {{ entry || 0 }}
        </span>
      </div>

      <div class="qty-hint">{{ hint }}</div>

      <v-layout row wrap class="qty-pad">
        <v-flex xs4 v-for="key in keys" :key="`key-${key}`">
          <v-btn flat block class="qty-key" @click="press(key)">{{ key }}</v-btn>
        </v-flex>
      </v-layout>

      <v-divider></v-divider>

      <div class="qty-foot">
        <v-btn flat class="qty-foot__btn" color="reject" @click.native="close()">
          Cancel
        </v-btn>
        <v-spacer></v-spacer>
        <!-- Turns solid green the moment a valid quantity is entered, so the
             action being armed is unmistakable. `ready` is the theme's green;
             `success` is mapped to orange in this app. -->
        <v-btn
          :flat="!isValid"
          :depressed="isValid"
          :dark="isValid"
          color="ready"
          class="qty-foot__btn qty-foot__confirm"
          :disabled="!isValid"
          @click.native="confirm()"
        >
          Food Ready
          <v-icon size="16" class="pl-1">flaticon-item-prepared</v-icon>
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.qty-head {
  background-color: #f5f5f5;
  text-align: center;
  font-size: 16px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.87);
  padding: 14px 12px;
}

.theme--dark .qty-head {
  background-color: #3a3a3a;
  color: #ffffff;
}

/* Matches the item rows' `subheading font-weight-bold`, so the item reads the
   same here as it does on the ticket. */
.qty-head__sub {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.25;
  margin-top: 4px;
}

.qty-hint {
  font-size: 12px;
  opacity: 0.7;
  text-align: right;
  padding: 2px 16px 0;
}

.qty-entry {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 4px;
}

.qty-entry__badge {
  background-color: #4a4a4a;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  padding: 4px 7px;
  border-radius: 3px;
}

.qty-entry__value {
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
}

.qty-entry__value--capped {
  color: #e53935;
}

.qty-pad {
  padding: 4px 0 6px;
}

/* Borderless keys -- the numerals themselves are the target, matching the POS
   pad. v-btn is kept for its ripple and touch handling. */
.qty-key.v-btn {
  min-width: 0;
  height: 52px;
  margin: 0;
  font-size: 24px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  touch-action: manipulation;
}

.qty-foot {
  display: flex;
  align-items: center;
  padding: 6px 8px;
}

.qty-foot__btn.v-btn {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}
</style>

<script>
// Mirrors the POS pad's shape. "All" takes the slot the POS uses for "00",
// which has no meaning for a quantity; "X" clears the entry.
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "X", "0", "All"];

// Keyed by the item status being applied, so the prompt always names the real
// action rather than assuming Food Ready.
const ACTION_MESSAGES = {
  3: "Select the quantity to Accept",
  4: "Select the quantity to mark as Food Ready",
  6: "Select the quantity to mark as Served",
  9: "Select the quantity to Void"
};

export default {
  name: "qty-picker",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object,
      default: null
    },
    // The item status this quantity will be applied to. Drives the prompt.
    status: {
      type: Number,
      default: 4
    }
  },
  data() {
    return {
      // v-dialog drives its own local flag, mirrored to the `show` prop -- the
      // same shape checkDetails.vue uses, without mutating the prop.
      dialog: false,
      entry: "",
      capped: false,
      keys: KEYS
    };
  },
  watch: {
    show(isOpen) {
      if (isOpen) this.reset();
      this.dialog = isOpen;
    },
    dialog(isOpen) {
      // Covers the paths v-dialog closes itself: overlay tap and Esc.
      if (!isOpen) this.close();
    }
  },
  computed: {
    totalQuantity() {
      return Number(this.item && this.item.quantity) || 0;
    },
    // check.vue rows carry `name`; the list view's rows carry `menu_name`.
    label() {
      return (this.item && (this.item.name || this.item.menu_name)) || "";
    },
    actionMessage() {
      return ACTION_MESSAGES[this.status] || "Select the quantity";
    },
    // States both ends of the valid range. A single-portion line has no range to
    // state, so it says what is available instead.
    hint() {
      if (this.totalQuantity <= 1) {
        return `Only ${this.totalQuantity} available`;
      }
      return `Enter a quantity from 1 to ${this.totalQuantity}`;
    },
    isValid() {
      const qty = Number(this.entry);
      return Number.isInteger(qty) && qty >= 1 && qty <= this.totalQuantity;
    }
  },
  methods: {
    reset() {
      this.entry = "";
      this.capped = false;
    },
    press(key) {
      if (key === "X") return this.reset();
      if (key === "All") {
        this.capped = false;
        this.entry = String(this.totalQuantity);
        return;
      }
      // A leading zero would only ever build an invalid quantity.
      if (!this.entry && key === "0") return;

      const next = Number(`${this.entry}${key}`);
      // The cap is enforced per keystroke, so an over-limit number can never be
      // entered in the first place.
      if (next > this.totalQuantity) {
        this.capped = true;
        // Reported through the app's own snackbar so the warning appears outside
        // the dialog (z-index 1000 vs the dialog's 6) instead of shifting its
        // layout. Same channel every other KDS error already uses.
        this.$store.commit("showSnackbar", {
          message: `Maximum quantity for this item is ${this.totalQuantity}`,
          color: "error"
        });
        return;
      }
      this.capped = false;
      this.entry = String(next);
    },
    confirm() {
      if (!this.isValid) return;
      this.$emit("select", { item: this.item, quantity: Number(this.entry) });
      this.close();
    },
    close() {
      this.dialog = false;
      if (this.show) this.$emit("update:show", false);
    }
  }
};
</script>
